import { SelectedFilter } from '@zoom/components';
import classnames from 'classnames';
import { equals } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RadioFilter, RangeFilter } from '../../types';
import { Item, SortedBulkLabels } from '../SortedBulkLabels';

type FilterConfigType<FilterName> = {
  dataTestId?: string;
  id: string;
  key: FilterName;
  label: string;
  defaultValue?: any;
  nameFormatter?: (value: any) => string;
};

type ActiveFiltersProps<FilterType, FilterName> = {
  config: FilterConfigType<FilterName>[];
  cancelFilterSidebar: () => void;
  isFixed?: boolean;
  openFilterSidebarItem: (itemId: string) => void;
  setSidebarFilter: (sidebarFilter: FilterType) => void;
  sidebarFilter: FilterType;
};

export function ActiveFilters<
  StateType extends Record<string, Item> | RangeFilter | RadioFilter,
  FilterType extends Record<string, StateType>,
  FilterName extends keyof FilterType
>(props: ActiveFiltersProps<FilterType, FilterName>) {
  const { t } = useTranslation();

  const { config, cancelFilterSidebar, isFixed, openFilterSidebarItem, setSidebarFilter, sidebarFilter } = props;

  const handleRemoveFilter = (filterKey: FilterConfigType<FilterName>['key'], defaultValue: StateType) => {
    cancelFilterSidebar();

    setSidebarFilter({
      ...sidebarFilter,
      [filterKey]: defaultValue,
    });
  };

  const countOfActiveFilters = config.reduce((activeFiltersCount, { key, defaultValue }) => {
    if (!equals(sidebarFilter[key], defaultValue)) {
      return activeFiltersCount + 1;
    }
    return activeFiltersCount;
  }, 0);

  if (countOfActiveFilters === 0) {
    return null;
  }

  const composeItems = (
    filterData: StateType,
    nameFormatter: FilterConfigType<FilterName>['nameFormatter']
  ): Item[] => {
    if (nameFormatter) {
      return [
        {
          id: '',
          name: nameFormatter(filterData),
        },
      ];
    }

    // Filter state is a record object
    const values = filterData && Object.values(filterData);

    const items = values.map((value) => ({
      id: value?.id ?? '',
      name: value?.name ?? '',
      subtitleValue: value?.subtitleValue ?? undefined,
      subtitleLabel: value?.subtitleLabel ?? undefined,
    }));

    return items;
  };

  return (
    <div
      className={classnames('active-filters', {
        'active-filters--fixed': isFixed,
      })}
    >
      <strong className="active-filters__title mr-2">
        {t('filter.activeFilter', { count: countOfActiveFilters })}:
      </strong>
      <div className="active-filters__tiles-wrapper">
        {config.map(({ id, key, label, dataTestId, defaultValue, nameFormatter }) => {
          if (equals(sidebarFilter[key], defaultValue)) {
            return null;
          }

          return (
            <SelectedFilter
              className="active-filters__tile"
              data-test-id={dataTestId}
              disabled={false}
              key={id}
              label={label}
              onClick={() => openFilterSidebarItem(id)}
              onRemove={() => handleRemoveFilter(key, defaultValue)}
              value={
                <SortedBulkLabels cursorPointer items={composeItems(sidebarFilter[key], nameFormatter)} selectedType />
              }
            />
          );
        })}
      </div>
    </div>
  );
}
